/**
 * Media Validation Utilities
 *
 * File type and size validation for media uploads
 *
 * SECURITY: Uses magic bytes validation to prevent malicious file uploads
 * disguised as images/videos (e.g., malware.exe renamed to malware.jpg)
 */

import {
  validateFileByMagicBytes,
  validateFileExtensionMatchesContent,
  detectFileTypeByMagicBytes,
} from './magicBytes';
import { devLog } from './logger';

export const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/bmp',
];

export const ACCEPTED_VIDEO_TYPES = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-msvideo',
];

export const ACCEPTED_PDF_TYPES = ['application/pdf'];

/**
 * Browser/OS-reported MIME types that are either uninformative (missing, or
 * the generic fallback used when a file isn't registered) or a legitimate
 * variant label for a video type we already accept (e.g. `.mov` reported as
 * `video/quicktime` even though we canonicalize detected content to
 * `video/mp4`; `.m4v` reported as `video/x-m4v`). These must NOT be
 * hard-rejected at the claimed-type gate — they fall through to magic-byte
 * content detection (steps 2-3 below), which makes the real accept/reject
 * call.
 */
const GENERIC_OR_VARIANT_CLAIMED_TYPES = [
  '',
  'application/octet-stream',
  'video/x-m4v',
  'video/mpeg',
];

// Per-type caps (mirrors backend per-type limits — cdb #317/#304-R2).
// Enforced client-side BEFORE any presign/upload call so oversize files never
// leave the browser; the server enforces the same caps as a defense-in-depth
// fallback (see media.service.ts `initiateUpload`).
export const MAX_VIDEO_FILE_SIZE = 100 * 1024 * 1024; // 100MB
export const MAX_IMAGE_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const MAX_PDF_FILE_SIZE = 20 * 1024 * 1024; // 20MB

// Absolute outer bound — kept for callers that don't need per-type nuance
// (e.g. the generic "up to X" hint text in MediaUploadZone).
export const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB
export const MAX_FILES = 10;

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Resolve the per-type max file size (bytes) and a human label for a MIME
 * type. Callers should pass the MAGIC-BYTE-DETECTED type when available
 * (falling back to the browser-claimed `file.type` only when detection
 * comes back empty) — browsers/OSes commonly report real MP4/MOV/M4V videos
 * with a generic/empty MIME (`''`, `application/octet-stream`), which would
 * otherwise fall into the Image branch and get capped/labeled incorrectly.
 */
function getMaxFileSizeForType(mimeType: string): { maxSize: number; label: string } {
  if (mimeType.startsWith('video/')) {
    return { maxSize: MAX_VIDEO_FILE_SIZE, label: 'Video' };
  }
  if (mimeType === 'application/pdf') {
    return { maxSize: MAX_PDF_FILE_SIZE, label: 'PDF' };
  }
  return { maxSize: MAX_IMAGE_FILE_SIZE, label: 'Image' };
}

/**
 * Validate a single file using magic bytes
 *
 * Three-layer security validation:
 * 1. Quick check: Validate claimed MIME type (from file extension)
 * 2. Deep check: Validate actual file content using magic bytes
 * 3. Consistency check: Verify extension matches content
 */
export async function validateFile(file: File): Promise<FileValidationResult> {
  try {
    const allTypes = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES, ...ACCEPTED_PDF_TYPES];

    // Step 1: Quick check - validate claimed MIME type.
    // Generic (missing/octet-stream) labels and known video MIME variants
    // (video/x-m4v, video/mpeg) are NOT rejected here — the browser's label
    // for these is unreliable across platforms, so we let content detection
    // (steps 2-3) make the real call instead of trusting the claim alone.
    const claimedTypeValid =
      allTypes.includes(file.type) || GENERIC_OR_VARIANT_CLAIMED_TYPES.includes(file.type);

    if (!claimedTypeValid) {
      devLog.warn(`[Security] File ${file.name} has invalid MIME type: ${file.type}`);
      return {
        valid: false,
        error: `File type not supported. Accepted: Images (JPEG, PNG, WebP, GIF, BMP), Videos (MP4, WebM, QuickTime, AVI), and PDFs`,
      };
    }

    // Step 2: Deep check - validate actual file content (magic bytes)
    const magicBytesValid = await validateFileByMagicBytes(file, allTypes);

    if (!magicBytesValid) {
      const detectedType = await detectFileTypeByMagicBytes(file);
      devLog.warn(
        `[Security] File ${file.name} failed magic bytes validation.`,
        `Claimed type: ${file.type}, Detected type: ${detectedType || 'unknown'}`
      );

      if (!detectedType) {
        return {
          valid: false,
          error: `File type not recognized. Please upload a valid image or video.`,
        };
      }

      return {
        valid: false,
        error: `File appears to be ${detectedType} but has wrong extension. Please use correct file extension.`,
      };
    }

    // Step 3: Verify extension matches content
    const extensionMatchesContent = await validateFileExtensionMatchesContent(file);

    if (!extensionMatchesContent) {
      const detectedType = await detectFileTypeByMagicBytes(file);
      devLog.warn(
        `[Security] File ${file.name} extension doesn't match content.`,
        `Extension suggests: ${file.type}, Magic bytes detect: ${detectedType}`
      );
      return {
        valid: false,
        error: `File extension doesn't match actual file content. This may be a security risk.`,
      };
    }

    // Step 4: Check file size against the per-type cap (mirrors server-side
    // per-type limits). Fires BEFORE any presign/upload call — see
    // MediaUploadZone.handleFiles, which awaits validateFiles() first.
    //
    // Key off the MAGIC-BYTE-DETECTED type, not the raw claimed `file.type`:
    // real MP4/MOV/M4V videos are commonly reported with a generic/empty
    // MIME (`''`, `application/octet-stream`) — see
    // GENERIC_OR_VARIANT_CLAIMED_TYPES above — which would otherwise land in
    // the Image branch and get wrongly capped at 10MB. Fall back to the
    // claimed type only if detection genuinely comes back empty.
    const detectedTypeForSizeCap = await detectFileTypeByMagicBytes(file);
    const { maxSize, label } = getMaxFileSizeForType(detectedTypeForSizeCap || file.type);
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `${label} is too large (${formatFileSize(file.size)}). Maximum is ${formatFileSize(maxSize)}.`,
      };
    }

    return { valid: true };
  } catch (error) {
    devLog.error(`[Security] Failed to validate file ${file.name}:`, error);
    return {
      valid: false,
      error: 'Failed to validate file. Please try again.',
    };
  }
}

/**
 * Validate multiple files (async - uses magic bytes validation)
 */
export async function validateFiles(files: File[]): Promise<FileValidationResult> {
  if (files.length === 0) {
    return {
      valid: false,
      error: 'No files selected',
    };
  }

  if (files.length > MAX_FILES) {
    return {
      valid: false,
      error: `Too many files (max ${MAX_FILES}). Selected: ${files.length}`,
    };
  }

  // Validate each file (async)
  for (const file of files) {
    const result = await validateFile(file);
    if (!result.valid) {
      return {
        valid: false,
        error: `${file.name}: ${result.error}`,
      };
    }
  }

  return { valid: true };
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Get file type (image, video, or pdf)
 */
export function getFileType(file: File): 'image' | 'video' | 'pdf' {
  if (file.type.startsWith('video/')) {
    return 'video';
  }
  if (file.type === 'application/pdf') {
    return 'pdf';
  }
  return 'image';
}

/**
 * Create blob URL for local preview
 */
export function createPreviewUrl(file: File): string {
  return URL.createObjectURL(file);
}

/**
 * Revoke blob URL to free memory
 */
export function revokePreviewUrl(url: string): void {
  URL.revokeObjectURL(url);
}
